import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { FileUpload } from "@/components/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, ExternalLink } from "lucide-react";

export default function ChefProfileEdit() {
  const { user, refresh } = useAuth();
  const [bio, setBio] = useState("");
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setBio(user.bio || "");
    setSpecialties(user.specialties || []);
    setAvatarUrl(user.avatar_url || null);
    setPortfolio(user.portfolio_photos || []);
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/me/profile", {
        bio,
        specialties,
        avatar_url: avatarUrl,
        portfolio_photos: portfolio,
      });
      await refresh();
      toast.success("Profile saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addSpecialty = () => {
    if (!specialtyInput.trim()) return;
    setSpecialties([...specialties, specialtyInput.trim()]);
    setSpecialtyInput("");
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="tick-label">Chef profile</div>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Your public page.</h1>
          </div>
          {user && (
            <Link
              data-testid="view-public-profile"
              to={`/chef/${user.id}`}
              className="btn-outline inline-flex items-center gap-2"
            ><ExternalLink size={13} /> View public page</Link>
          )}
        </div>

        <div className="mt-10 space-y-8">
          <div>
            <div className="tick-label mb-2">Avatar</div>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border hairline overflow-hidden bg-neutral-900">
                {avatarUrl ? <img src={fileUrl(avatarUrl)} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <FileUpload
                testId="profile-avatar-upload"
                label="Upload avatar"
                accept="image/*"
                onUploaded={(res) => setAvatarUrl(res.path)}
              />
            </div>
          </div>

          <div>
            <div className="tick-label mb-2">Bio</div>
            <textarea
              data-testid="profile-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={6}
              placeholder="Tell hosts and clients about your style, training, and what you cook best…"
              className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
            />
          </div>

          <div>
            <div className="tick-label mb-2">Specialties</div>
            <div className="flex gap-2">
              <input
                data-testid="profile-specialty-input"
                value={specialtyInput}
                onChange={(e) => setSpecialtyInput(e.target.value)}
                placeholder="e.g. Neapolitan pizza"
                className="flex-1 border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
              />
              <button type="button" data-testid="profile-specialty-add" onClick={addSpecialty} className="btn-outline">Add</button>
            </div>
            {specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {specialties.map((s, i) => (
                  <span key={i} className="border hairline px-3 py-1.5 text-sm flex items-center gap-2">
                    {s} <button type="button" onClick={() => setSpecialties(specialties.filter((_, j) => j !== i))}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="tick-label mb-2">Portfolio photos</div>
            <FileUpload
              testId="profile-portfolio-upload"
              label="Add photo"
              accept="image/*"
              onUploaded={(res) => setPortfolio([...portfolio, res.path])}
            />
            {portfolio.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {portfolio.map((p, i) => (
                  <div key={i} className="relative aspect-square bg-neutral-900 border hairline overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPortfolio(portfolio.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 p-1">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button data-testid="profile-save" onClick={save} disabled={busy} className="btn-signal disabled:opacity-50">
            {busy ? "..." : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
